import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { aiModelService } from '../services/ai-model.service';
import ModelProviderDropdown from './ModelProviderDropdown';
import ModelNameDropdown from './ModelNameDropdown';
import { 
  FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaCheck, 
  FaExclamationCircle, FaRobot, FaBrain, FaKey, FaSync,
  FaGoogle, FaOpenid, FaCloud, FaRocket, FaCog, FaToggleOn, FaToggleOff
} from 'react-icons/fa';

// Styled components
const PageContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background-color: #1a1d23;
  color: #ffffff;
`;

const ActionButton = styled.button`
    margin: 0 2px;
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 400;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    transition: all 0.2s;
    background-color: ${props => props.primary ? '#0073e6' : props.danger ? '#dc3545' : '#004a9f'};
    color: ${props => (props.primary || props.danger || props.info) ? 'white' : '#e0f0ff'};
    border: 1px solid ${props => props.primary ? '#0062cc' : props.danger ? '#bd2130' : '#003776'};
    
    &:hover {
      background-color: ${props => props.primary ? '#0062cc' : props.danger ? '#c82333' : '#003776'};
      border-color: ${props => props.primary ? '#0056b3' : props.danger ? '#a71d2a' : '#002c5c'};
    }
  `;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid #2f343a;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 500;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  
  svg {
    color: #0073e6;
  }
`;

// Use the enhanced Button component instead of a separate AddButton component

const Card = styled.div`
  background: #2f343a;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 20px;
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #2f343a;
  border-radius: 4px;
  width: 500px;
  max-width: 90%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #3b424a;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #a0c0e0;
  &:hover {
    color: #ffffff;
  }
`;

const ModalBody = styled.div`
  padding: 16px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  flex: 1;
  min-width: 200px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: #999;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border-radius: 3px;
  border: 1px solid #3b424a;
  font-size: 14px;
  &:focus {
    border-color: #0073e6;
    outline: none;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border-radius: 3px;
  border: 1px solid #3b424a;
  font-size: 14px;
  background-color: #2f343a;
  &:focus {
    border-color: #0073e6;
    outline: none;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
  }
`;

const Dropdown = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownContent = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: #2f343a;
  min-width: 200px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  z-index: 1000;
  display: ${props => props.show ? 'block' : 'none'};
`;

const DropdownItem = styled.div`
  padding: 10px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background-color: #3b424a;
  }
  
  svg {
    color: ${props => {
      switch(props.provider) {
        case 'openai': return '#10a37f';
        // case 'google': return '#4285f4';
        // case 'anthropic': return '#b668ff';
        // case 'groq': return '#ff6b6b';
        default: return '#1890ff';
      }
    }};
  }
`;

const Button = styled.button`
  padding: ${props => props.small ? '5px 10px' : '6px 12px'};
  border-radius: 4px;
  background: ${props => 
    props.primary ? '#007bff' : 
    props.danger ? '#dc3545' : 
    props.success ? '#28a745' : 
    props.warning ? '#ffc107' : 
    props.info ? '#17a2b8' : '#f8f9fa'};
  color: ${props => (props.primary || props.danger || props.success || props.info) ? 'white' : 
    props.warning ? '#212529' : '#6c757d'};
  border: none;
  font-weight: 500;
  font-size: ${props => props.small ? '12px' : '14px'};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-shadow: none;
  
  &:hover:not(:disabled) { 
    background: ${props => 
      props.primary ? '#0069d9' : 
      props.danger ? '#c82333' : 
      props.success ? '#218838' : 
      props.warning ? '#e0a800' : 
      props.info ? '#138496' : '#e2e6ea'};
    opacity: 0.95;
  }
  
  &:active:not(:disabled) {
    opacity: 1;
  }
  
  &:disabled { 
    background: #e9ecef; 
    color: #adb5bd; 
    cursor: not-allowed; 
  }
  
  svg {
    font-size: ${props => props.small ? '12px' : '14px'};
  }
`;

const Alert = styled.div`
  margin-top: 12px;
  padding: 8px 12px;
  border-radius: 3px;
  font-size: 14px;
  display: flex;
  align-items: center;
  background: ${p => {
    switch(p.type) {
      case 'error': return '#3a1c1c';
      case 'success': return '#1c3a1c';
      case 'warning': return '#3a3a1c';
      case 'info': return '#1c1c3a';
      default: return '#1c1c3a';
    }
  }};
  color: ${p => {
    switch(p.type) {
      case 'error': return '#ffb3b3';
      case 'success': return '#b3ffb3';
      case 'warning': return '#ffffb3';
      case 'info': return '#b3b3ff';
      default: return '#b3b3ff';
    }
  }};
  border: 1px solid ${p => {
    switch(p.type) {
      case 'error': return '#5c2929';
      case 'success': return '#295c29';
      case 'warning': return '#5c5c29';
      case 'info': return '#29295c';
      default: return '#29295c';
    }
  }};
  
  svg {
    margin-right: 8px;
    font-size: 16px;
    color: ${p => {
      switch(p.type) {
        case 'error': return '#ff8080';
        case 'success': return '#80ff80';
        case 'warning': return '#ffff80';
        case 'info': return '#8080ff';
        default: return '#8080ff';
      }
    }};
  }
`;

const AlertIcon = styled.span`
  margin-right: 8px;
  display: flex;
  align-items: center;
  font-size: 14px;
`;

const Badge = styled.span`
  background: #e6f7ff;
  color: #1890ff;
  border-radius: 2px;
  padding: 1px 8px;
  margin-left: 8px;
  font-size: 12px;
  border: 1px solid #91d5ff;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-weight: 500;
  color: #ffffff;
  background: #2f343a;
  border-bottom: 1px solid #3b424a;
`;

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #3b424a;
  color: #ffffff;
`;

const ActionCell = styled(Td)`
  text-align: right;
  white-space: nowrap;
  padding: 8px 16px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
`;

const SubTabsContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #3b424a;
`;

const SubTab = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 4px 4px 0 0;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  background: ${props => props.active ? '#2f343a' : '#1a1d23'};
  color: ${props => props.active ? '#ffffff' : '#999'};
  border-bottom: ${props => props.active ? '3px solid #0073e6' : 'none'};
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 20px;
  color: #a0c0e0;
`;

const EmptyTableMessage = styled.div`
  text-align: center;
  padding: 30px;
  color: #a0c0e0;
  font-size: 15px;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => props.active ? '#1c3a1c' : '#3a1c1c'};
  color: ${props => props.active ? '#80ff80' : '#ff8080'};
  border: 1px solid ${props => props.active ? '#295c29' : '#5c2929'};
`;

const ProviderIcon = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 8px;
  color: ${props => {
    switch(props.provider) {
      case 'openai': return '#10a37f';
      case 'google': return '#4285f4';
      case 'anthropic': return '#b668ff';
      case 'groq': return '#ff6b6b';
      case 'ollama': return '#ff9900';
      default: return '#0073e6';
    }
  }};
`;

const ToggleSwitch = styled.button`
  background: none;
  border: none;
  color: ${props => props.active ? '#80ff80' : '#a0c0e0'};
  cursor: pointer;
  font-size: 20px;
  display: flex;
  align-items: center;
  transition: color 0.2s;
  padding: 0;
  margin-right: 8px;
  
  &:hover {
    color: ${props => props.active ? '#b3ffb3' : '#ff8080'};
  }
  
  &:focus {
    outline: none;
  }
`;

// Using the imported ModelProviderDropdown component

// AIModelAddModal component
function AIModelAddModal({ isOpen, onClose, onSuccess }) {
  const [provider, setProvider] = useState('openai');
  const [modelName, setModelName] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const res = await aiModelService.create({ 
        provider, 
        model_name: modelName, 
        api_key: apiKey 
      });
      
      if (res.success) {
        onSuccess(res.model);
        setProvider('openai');
        setModelName('');
        setApiKey('');
        onClose();
      } else {
        setError('Could not create AI model configuration.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create AI model configuration.');
    }
    
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            <FaBrain style={{ color: '#1890ff' }} />
            Add New AI Model
          </ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <FormLabel htmlFor="provider">Provider</FormLabel>
              <ModelProviderDropdown
                value={provider}
                onChange={setProvider}
                required
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="model-name">Model Name</FormLabel>
              <ModelNameDropdown
                id="model-name"
                value={modelName}
                onChange={setModelName}
                provider={provider}
                required
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="api-key">API Key</FormLabel>
              <Input
                id="api-key"
                type="password"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder="Enter API key"
                required
              />
            </FormGroup>
            {error && (
              <Alert type="error">
                <AlertIcon><FaExclamationCircle /></AlertIcon>
                {error}
              </Alert>
            )}
            <ModalFooter>
              <Button type="button" onClick={onClose}><FaTimes /> Cancel</Button>
              <Button primary type="submit" disabled={loading}>
                {loading ? <><FaSync className="fa-spin" /> Creating...</> : <><FaPlus /> Add Model</>}
              </Button>
            </ModalFooter>
          </Form>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
}

// AIModelEditModal component
function AIModelEditModal({ isOpen, onClose, onSuccess, model }) {
  const [provider, setProvider] = useState('');
  const [modelName, setModelName] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (model) {
      setProvider(model.provider);
      setModelName(model.model_name);
      setApiKey(''); // Don't set the API key for security reasons
      setIsActive(model.is_active);
    }
  }, [model]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const updates = {
        provider,
        model_name: modelName,
        is_active: isActive
      };
      
      // Only include API key if it was changed
      if (apiKey) {
        updates.api_key = apiKey;
      }
      
      const res = await aiModelService.update(model.id, updates);
      
      if (res.success) {
        onSuccess();
        onClose();
      } else {
        setError('Could not update AI model configuration.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update AI model configuration.');
    }
    
    setLoading(false);
  };

  if (!isOpen || !model) return null;

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            <FaEdit style={{ color: '#1890ff' }} />
            Edit AI Model
          </ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <FormLabel htmlFor="provider">Provider</FormLabel>
              <ModelProviderDropdown
                value={provider}
                onChange={setProvider}
                required
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="model-name">Model Name</FormLabel>
              <ModelNameDropdown
                id="model-name"
                value={modelName}
                onChange={setModelName}
                provider={provider}
                required
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="api-key">API Key (leave blank to keep current)</FormLabel>
              <Input
                id="api-key"
                type="password"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder="Enter new API key or leave blank"
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="is-active">Status</FormLabel>
              <Select
                id="is-active"
                value={isActive.toString()}
                onChange={e => setIsActive(e.target.value === 'true')}
                required
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Select>
            </FormGroup>
            {error && (
              <Alert type="error">
                <AlertIcon><FaExclamationCircle /></AlertIcon>
                {error}
              </Alert>
            )}
            <ModalFooter>
              <Button type="button" onClick={onClose}><FaTimes /> Cancel</Button>
              <Button primary type="submit" disabled={loading}>
                {loading ? <><FaSync className="fa-spin" /> Saving...</> : <><FaSave /> Save Changes</>}
              </Button>
            </ModalFooter>
          </Form>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
}

// Main AIModelManager component
export default function AIModelManager() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [testingModel, setTestingModel] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('all');

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    setLoading(true);
    try {
      const res = await aiModelService.list();
      if (res && res.models) {
        setModels(res.models);
        setMessage({ type: '', text: '' });
      } else {
        console.error('Invalid response format:', res);
        setModels([]);
        setMessage({ 
          type: 'error', 
          text: 'Failed to load AI models: Invalid response format' 
        });
      }
    } catch (e) {
      console.error('Error fetching AI models:', e);
      setModels([]);
      setMessage({ 
        type: 'error', 
        text: e.response?.data?.message || 'API connection failed. Please check server connection.' 
      });
    }
    setLoading(false);
  };

  const handleModelCreated = (model) => {
    setMessage({ 
      type: 'success', 
      text: `AI model '${model.provider}/${model.model_name}' created successfully!` 
    });
    fetchModels();
  };

  const handleModelUpdated = () => {
    setMessage({ 
      type: 'success', 
      text: 'AI model updated successfully' 
    });
    fetchModels();
  };

  const openEditModal = (model) => {
    setSelectedModel(model);
    setShowEditModal(true);
  };

  const deleteModel = async (id) => {
    if (!window.confirm('Are you sure you want to delete this AI model configuration?')) return;
    
    setMessage({ type: '', text: '' });
    try {
      await aiModelService.delete(id);
      setMessage({ type: 'success', text: 'AI model deleted successfully' });
      fetchModels();
    } catch (e) {
      setMessage({ 
        type: 'error', 
        text: e.response?.data?.error || 'Failed to delete AI model' 
      });
    }
  };

  const testConnection = async (id) => {
    setTestingModel(id);
    setMessage({ type: '', text: '' });
    try {
      const res = await aiModelService.testConnection(id);
      if (res.success) {
        setMessage({ type: 'success', text: res.message || 'Connection test successful' });
      } else {
        setMessage({ type: 'error', text: res.message || 'Connection test failed' });
      }
    } catch (e) {
      setMessage({ 
        type: 'error', 
        text: e.response?.data?.error || 'Failed to test connection' 
      });
    }
    setTestingModel(null);
  };

  const toggleModelStatus = async (id) => {
    try {
      setMessage({ text: '', type: '' });
      
      // Find the model to update its status in the UI immediately
      const modelIndex = models.findIndex(m => m.id === id);
      if (modelIndex === -1) return;
      
      // Optimistically update the UI
      const updatedModels = [...models];
      updatedModels[modelIndex] = {
        ...updatedModels[modelIndex],
        is_active: !updatedModels[modelIndex].is_active
      };
      setModels(updatedModels);
      
      // Call the API to toggle the status
      const response = await aiModelService.toggleModelStatus(id);
      
      if (response.success) {
        setMessage({
          text: response.message || `Model ${response.model.is_active ? 'activated' : 'deactivated'} successfully`,
          type: 'success'
        });
        
        // Update with the actual response from the server
        fetchModels();
      } else {
        // If the API call fails, revert the optimistic update
        const revertedModels = [...models];
        setModels(revertedModels);
        
        setMessage({
          text: response.message || 'Failed to toggle model status',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error toggling model status:', error);
      
      // Revert the optimistic update
      fetchModels();
      
      setMessage({
        text: error.response?.data?.message || 'Failed to toggle model status',
        type: 'error'
      });
    }
  };

  const getProviderIcon = (provider) => {
    switch(provider) {
      case 'openai':
        return <FaOpenid />;
      // case 'google':
      //   return <FaGoogle />;
      // case 'anthropic':
      //   return <FaCloud />;
      // case 'groq':
      //   return <FaRocket />;
      default:
        return <FaRobot />;
    }
  };

  const getProviderName = (provider) => {
    switch(provider) {
      case 'openai':
        return 'OpenAI';
      // case 'google':
      //   return 'Google (Gemini)';
      // case 'anthropic':
      //   return 'Anthropic (Claude)';
      // case 'groq':
      //   return 'Groq';
      default:
        return provider;
    }
  };

  // Filter models based on active sub-tab
  const filteredModels = models.filter(model => {
    if (activeSubTab === 'all') return true;
    if (activeSubTab === 'active') return model.is_active;
    if (activeSubTab === 'inactive') return !model.is_active;
    return true;
  });

  return (
    <PageContainer >
      <PageHeader>
        <PageTitle><FaBrain /> AI Model Configuration</PageTitle>
        <Button primary onClick={() => setShowAddModal(true)}>
          <FaPlus /> Add Model
        </Button>
      </PageHeader>
      
      {/* Add Model Modal */}
      <AIModelAddModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleModelCreated}
      />
      
      {/* Edit Model Modal */}
      <AIModelEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={handleModelUpdated}
        model={selectedModel}
      />
      
      {/* Feedback messages */}
      {message.text && (
        <Alert type={message.type}>
          <AlertIcon>
            {message.type === 'success' ? <FaCheck /> : 
             message.type === 'warning' ? <FaExclamationCircle /> : 
             <FaExclamationCircle />}
          </AlertIcon>
          {message.text}
        </Alert>
      )}
      
      {/* Sub-tabs for model filtering */}
      <SubTabsContainer>
        <SubTab 
          active={activeSubTab === 'all'} 
          onClick={() => setActiveSubTab('all')}
        >
          All Models
        </SubTab>
        <SubTab 
          active={activeSubTab === 'active'} 
          onClick={() => setActiveSubTab('active')}
        >
          Active Models
        </SubTab>
        <SubTab 
          active={activeSubTab === 'inactive'} 
          onClick={() => setActiveSubTab('inactive')}
        >
          Inactive Models
        </SubTab>
      </SubTabsContainer>
      
      {/* AI Models table */}
      <Card>
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <Th><div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FaRobot /> Provider</div></Th>
                <Th><div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FaBrain /> Model</div></Th>
                <Th><div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FaKey /> API Key</div></Th>
                <Th><div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FaCheck /> Status</div></Th>
                <Th style={{ textAlign: 'right' }}><div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}><FaCog /> Actions</div></Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <Td colSpan="5">
                    <LoadingText>Loading AI models...</LoadingText>
                  </Td>
                </tr>
              ) : filteredModels.length === 0 ? (
                <tr>
                  <Td colSpan="5">
                    <EmptyTableMessage>
                      No AI models configured. Click "Add Model" to create one.
                    </EmptyTableMessage>
                  </Td>
                </tr>
              ) : (
                filteredModels.map((model) => (
                  <tr key={model.id}>
                    <Td>
                      <ProviderIcon provider={model.provider}>
                        {getProviderIcon(model.provider)}
                      </ProviderIcon>
                      {getProviderName(model.provider)}
                    </Td>
                    <Td>{model.model_name}</Td>
                    <Td>{model.masked_key}</Td>
                    <Td>
                      <StatusBadge active={model.is_active}>
                        {model.is_active ? 'Active' : 'Inactive'}
                      </StatusBadge>
                    </Td>
                    <ActionCell>
                      <ToggleSwitch 
                        active={model.is_active}
                        onClick={() => toggleModelStatus(model.id)}
                        title={model.is_active ? 'Deactivate model' : 'Activate model'}
                      >
                        {model.is_active ? <FaToggleOn /> : <FaToggleOff />}
                      </ToggleSwitch>
                      <ActionButton small primary onClick={() => openEditModal(model)}>
                        <FaEdit /> Edit
                      </ActionButton>
                      <ActionButton 
                        small
                        info
                        onClick={() => testConnection(model.id)}
                        disabled={testingModel === model.id}
                      >
                        {testingModel === model.id ? <FaSync className="fa-spin" /> : <FaKey />}{' '}
                        {testingModel === model.id ? 'Testing...' : 'Test'}
                      </ActionButton>
                      <ActionButton small danger onClick={() => deleteModel(model.id)}>
                        <FaTrash /> Delete
                      </ActionButton>
                    </ActionCell>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </TableContainer>
      </Card>
    </PageContainer>
  );
}
